import { inject, Injectable } from "@angular/core";
import { ActivatedRouteSnapshot, CanActivate, CanActivateFn, Router, RouterStateSnapshot } from "@angular/router";
import { filter, map, Observable, of, switchMap, take } from "rxjs";

import { PATHS } from "@app/core/constants/path.const";
import { isLoggedIn, selectMustChangePassword } from "@app/features/auth/store/auth.selectors";
import { Store } from "@ngrx/store";

@Injectable()
export class AuthGuard implements CanActivate {

  constructor(
    private readonly store: Store,
    private readonly router: Router,
  ) {}

  canActivate(_: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
    return this.waitLoadCurrentUser().pipe(
      switchMap((isLoggedIn) => {
        if (!isLoggedIn) {
          return this.router.navigate(
            [PATHS.auth.path, PATHS.auth.login.path], {
              queryParams: { page: state.url },
              queryParamsHandling: "merge"
            }
          );
        }

        return of(true);
      })
    );
  }

  private waitLoadCurrentUser(): Observable<boolean> {
    return this.store.select(isLoggedIn).pipe(
      filter((isLoggedIn) => typeof isLoggedIn === "boolean"),
      map((isLoggedIn) => !!isLoggedIn)
    );
  }
}

const FORCE_PASSWORD_CHANGE_URL = `/${PATHS.forcePasswordChange.path}`;

export const mustChangePasswordGuard: CanActivateFn = (_, state) => {
  const store = inject(Store);
  const router = inject(Router);

  return store.select(selectMustChangePassword).pipe(
    take(1),
    map((mustChangePassword) => {
      if (!mustChangePassword || state.url.startsWith(FORCE_PASSWORD_CHANGE_URL)) {
        return true;
      }

      return router.createUrlTree([PATHS.forcePasswordChange.path]);
    }),
  );
};
